import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatPrice, toImg } from "@/api/client";
import { useFavorites } from "@/context/FavoritesContext";
import SEO from "@/components/SEO";
import { X, Check, Minus } from "lucide-react";

export default function ComparePage() {
  const { slugs } = useFavorites();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const results = [];
      for (const s of slugs) {
        try { const { data } = await api.get(`/properties/${s}`); results.push(data); }
        catch { /* skip missing */ }
      }
      if (!cancelled) {
        setItems(results);
        setSelected(results.slice(0, Math.min(2, results.length)).map((r) => r.slug));
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [slugs]);

  const toggle = (slug) => {
    setSelected((cur) => {
      if (cur.includes(slug)) return cur.filter((s) => s !== slug);
      if (cur.length >= 4) return cur;
      return [...cur, slug];
    });
  };

  const chosen = items.filter((i) => selected.includes(i.slug));
  const allFeatures = Array.from(new Set(chosen.flatMap((c) => c.features || [])));

  const cellCls = "px-4 py-3 border-b border-stone-100 text-sm";

  return (
    <div data-testid="page-comparar">
      <SEO title="Comparar imóveis" description="Compare os seus imóveis favoritos lado a lado." />
      <section className="container-cnp pt-14 lg:pt-20 pb-8">
        <span className="tag-chip">Comparar</span>
        <h1 className="mt-4 font-display text-4xl md:text-5xl text-stone-900">Compare os seus favoritos.</h1>
        <p className="mt-4 max-w-2xl text-stone-600">Escolha até 4 imóveis dos seus favoritos e veja-os lado a lado.</p>
      </section>

      {loading ? (
        <div className="container-cnp py-24 text-stone-500">A carregar...</div>
      ) : items.length === 0 ? (
        <section className="container-cnp pb-24">
          <div className="card-soft p-12 text-center">
            <h2 className="font-display text-2xl text-stone-900">Sem favoritos para comparar.</h2>
            <p className="mt-2 text-stone-600">Adicione imóveis aos favoritos primeiro.</p>
            <Link to="/imoveis" className="btn-primary mt-6 inline-flex">Ver imóveis</Link>
          </div>
        </section>
      ) : (
        <>
          <section className="container-cnp pb-6">
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-3">Escolher imóveis a comparar ({selected.length}/4)</div>
            <div className="flex flex-wrap gap-2">
              {items.map((p) => (
                <button
                  key={p.slug}
                  type="button"
                  onClick={() => toggle(p.slug)}
                  data-testid={`compare-toggle-${p.slug}`}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${
                    selected.includes(p.slug)
                      ? "border-[#103C4A] bg-[#103C4A] text-white"
                      : "border-stone-200 bg-white text-stone-700 hover:border-[#103C4A]"
                  }`}
                >
                  {selected.includes(p.slug) ? <Check className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                  <span className="max-w-[220px] truncate">{p.title}</span>
                </button>
              ))}
            </div>
          </section>

          {chosen.length >= 2 && (
            <section className="container-cnp pb-24 overflow-x-auto">
              <table className="w-full min-w-[720px] card-soft" data-testid="compare-table">
                <thead>
                  <tr>
                    <th className="w-40 px-4 py-4 text-left text-xs uppercase tracking-widest text-stone-500 border-b border-stone-200">Atributo</th>
                    {chosen.map((p) => (
                      <th key={p.slug} className="px-4 py-4 text-left border-b border-stone-200 min-w-[220px]">
                        <div className="aspect-[16/10] rounded-lg overflow-hidden bg-stone-100 mb-3">
                          <img src={toImg(p.main_image)} alt={p.title} className="h-full w-full object-cover" />
                        </div>
                        <Link to={`/imoveis/${p.slug}`} className="font-medium text-stone-900 text-sm leading-tight block link-underline">{p.title}</Link>
                        <button onClick={() => toggle(p.slug)} className="mt-2 text-xs text-stone-500 hover:text-red-700 inline-flex items-center gap-1"><X className="h-3 w-3" /> Remover</button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr><td className={cellCls + " font-medium text-stone-500"}>Preço</td>{chosen.map((p) => <td key={p.slug} className={cellCls + " font-display text-xl text-[#103C4A]"}>{formatPrice(p.price, p.currency)}</td>)}</tr>
                  <tr><td className={cellCls + " font-medium text-stone-500"}>Localidade</td>{chosen.map((p) => <td key={p.slug} className={cellCls}>{p.locality}</td>)}</tr>
                  <tr><td className={cellCls + " font-medium text-stone-500"}>Tipo</td>{chosen.map((p) => <td key={p.slug} className={cellCls}>{p.property_type}</td>)}</tr>
                  <tr><td className={cellCls + " font-medium text-stone-500"}>Tipologia</td>{chosen.map((p) => <td key={p.slug} className={cellCls}>{p.typology}</td>)}</tr>
                  <tr><td className={cellCls + " font-medium text-stone-500"}>Área</td>{chosen.map((p) => <td key={p.slug} className={cellCls}>{p.area ? `${p.area} m²` : "—"}</td>)}</tr>
                  <tr><td className={cellCls + " font-medium text-stone-500"}>€/m²</td>{chosen.map((p) => <td key={p.slug} className={cellCls}>{p.area ? formatPrice(Math.round(p.price / p.area), p.currency) : "—"}</td>)}</tr>
                  <tr><td className={cellCls + " font-medium text-stone-500"}>Estado</td>{chosen.map((p) => <td key={p.slug} className={cellCls}>{p.status}</td>)}</tr>
                  {allFeatures.map((f) => (
                    <tr key={f}><td className={cellCls + " text-stone-500"}>{f}</td>{chosen.map((p) => <td key={p.slug} className={cellCls}>{(p.features || []).includes(f) ? <Check className="h-4 w-4 text-[#103C4A]" /> : <Minus className="h-4 w-4 text-stone-300" />}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
          {chosen.length < 2 && (
            <section className="container-cnp pb-24">
              <div className="card-soft p-10 text-center text-stone-600">Escolha pelo menos 2 imóveis para começar a comparação.</div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
